import { useState, useEffect, useRef } from "react";
import { LucideSend, LucideLoader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Modal } from "@/components/modals/Modal";
import { BACKEND_URL } from "@/constants/api";
import { useAuth } from "@clerk/clerk-react";
import { buildUserPrompt } from "@/reports/shared_utils/buildUserPrompt";
import { useSubscription } from "@/context/SubscriptionContext";
import { toast } from "sonner";

const SYSTEM_PROMPT_LIMIT = 3000;
const INSTRUCTIONS_LIMIT = 1000;
const TYPEWRITER_MS = 12;

function autoResizeTextarea(el) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
}

function formatKey(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

export function AICommentModal({
  open,
  onClose,
  defaultSystemPrompt,
  studentReportInfo,
  currentComment,
  reportId,
  onCommentGenerated,
}) {
  const { getToken } = useAuth();
  const { aiCreditsRemaining, seatInfo, refetch: refetchSubscription } = useSubscription();

  const schoolActive = seatInfo?.status === "active";
  const schoolRemaining = schoolActive ? seatInfo.aiMax - seatInfo.aiGensUsed : null;
  const schoolIsExhausted = schoolActive && schoolRemaining <= 0;

  const schoolLineColor = !schoolActive
    ? "text-muted-foreground"
    : schoolRemaining > 0 ? "text-success" : "text-destructive";

  const personalIsStandby = schoolActive && !schoolIsExhausted;
  const personalLineColor = personalIsStandby
    ? "text-muted-foreground"
    : (aiCreditsRemaining ?? 0) > 0 ? "text-success" : "text-destructive";

  const [systemPrompt, setSystemPrompt] = useState(defaultSystemPrompt || "");
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // 'input' = initial setup; 'result' = reviewing / refining generated text
  const [phase, setPhase] = useState("input");
  // What the AI sees as "existing comment" — updated to the displayed text on each regeneration
  const [iterationComment, setIterationComment] = useState(currentComment || "");
  // The generated text being typed out, then editable
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const typewriterRef = useRef(null);
  const generatedTextareaRef = useRef(null);

  useEffect(() => {
    if (open) {
      clearInterval(typewriterRef.current);
      setSystemPrompt(defaultSystemPrompt || "");
      setAdditionalInstructions("");
      setShowSystemPrompt(false);
      setPhase("input");
      setIterationComment(currentComment || "");
      setDisplayedText("");
      setIsTyping(false);
    }
  }, [open, defaultSystemPrompt, currentComment]);

  useEffect(() => {
    return () => clearInterval(typewriterRef.current);
  }, []);

  useEffect(() => {
    autoResizeTextarea(generatedTextareaRef.current);
  }, [displayedText]);

  const startTypewriter = (text) => {
    setDisplayedText("");
    setIsTyping(true);
    let i = 0;
    typewriterRef.current = setInterval(() => {
      i++;
      setDisplayedText(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(typewriterRef.current);
        setIsTyping(false);
      }
    }, TYPEWRITER_MS);
  };

  // contextComment: when regenerating, the current displayedText is passed so we
  // don't rely on a stale state value. On first generation it is undefined.
  const handleGenerate = async (contextComment) => {
    setIsGenerating(true);
    const token = await getToken();
    if (!token) {
      setIsGenerating(false);
      toast.error("Sign in to use AI comment generation.");
      return;
    }

    const isRegeneration = contextComment !== undefined;
    const baseComment = isRegeneration ? contextComment : iterationComment;

    try {
      const userPrompt = buildUserPrompt(baseComment, additionalInstructions);
      const res = await fetch(`${BACKEND_URL}api/report-comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reportId: reportId || "free-trial",
          systemPrompt,
          userPrompt,
          // Send field names only - backend fetches values from DB to prevent client-supplied data reaching the AI prompt.
          reportInfoFields: Object.keys(studentReportInfo || {}),
        }),
      });

      if (res.status === 429) {
        const body = await res.json().catch(() => ({}));
        const retryMins = Math.ceil((body.retryAfterSeconds ?? 900) / 60);
        toast.error("Too many requests.", {
          description: `Please wait approximately ${retryMins} minute${retryMins !== 1 ? "s" : ""} before trying again.`,
        });
        return;
      }
      if (res.status === 403) {
        toast.error("You've run out of AI credits.", {
          description: "Purchase more credits to keep generating comments.",
          action: { label: "Buy credits", onClick: () => window.open("/credits-and-billing", "_blank") },
        });
        return;
      }
      if (!res.ok) {
        toast.error("Failed to generate AI comment. Please try again.");
        return;
      }

      const { comment } = await res.json();
      refetchSubscription();

      if (isRegeneration) {
        setIterationComment(contextComment);
      }
      setAdditionalInstructions("");
      setPhase("result");
      startTypewriter(comment);
    } catch {
      toast.error("Failed to generate AI comment. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAcceptAdd = () => {
    const combined = currentComment?.trim()
      ? currentComment.trim() + "\n\n" + displayedText
      : displayedText;
    onCommentGenerated(combined);
    onClose();
  };

  const handleAcceptReplace = () => {
    onCommentGenerated(displayedText);
    onClose();
  };

  const infoEntries = Object.entries(studentReportInfo || {}).filter(
    ([, v]) => v !== undefined && v !== null && v !== ""
  );

  const busy = isGenerating || isTyping;
  const canRegenerate = !busy && additionalInstructions.trim().length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Generate with AI"
      icon={<LucideSend size={16} className="text-primary" />}
      maxWidth="max-w-2xl"
      maxHeight="max-h-[90vh]"
    >
      <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">

        {/* System Prompt - collapsible */}
        <div className="space-y-1.5 border-t border-border pt-4">
          <div className="flex items-center justify-between">
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              System Prompt
            </label>
            <button
              type="button"
              onClick={() => setShowSystemPrompt((v) => !v)}
              className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"
              disabled={busy}
            >
              {showSystemPrompt ? "Hide" : "Edit"}
            </button>
          </div>
          {!showSystemPrompt && (
            <p className="text-xs text-muted-foreground italic">Using default AI behavior.</p>
          )}
          {showSystemPrompt && (
            <>
              <Textarea
                value={systemPrompt}
                onChange={(e) => {
                  setSystemPrompt(e.target.value.slice(0, SYSTEM_PROMPT_LIMIT));
                  autoResizeTextarea(e.target);
                }}
                ref={(el) => el && autoResizeTextarea(el)}
                className="resize-none text-sm font-mono overflow-hidden"
                disabled={busy}
              />
              <p className={`text-xs text-right ${systemPrompt.length > SYSTEM_PROMPT_LIMIT * 0.9 ? "text-destructive" : "text-muted-foreground"}`}>
                {systemPrompt.length}/{SYSTEM_PROMPT_LIMIT}
              </p>
              <p className="text-xs text-muted-foreground">Advanced: controls tone and style.</p>
            </>
          )}
        </div>

        {/* Report Info — input phase only */}
        {phase === "input" && infoEntries.length > 0 && (
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block">
              Report Info
            </label>
            <div className="rounded-lg border border-border bg-muted/50 p-3">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                {infoEntries.map(([key, value]) => (
                  <div key={key} className="flex gap-1.5 min-w-0">
                    <dt className="text-[11px] text-muted-foreground shrink-0">{formatKey(key)}:</dt>
                    <dd className="text-[11px] text-foreground font-medium truncate">{String(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        )}

        {/* Existing Comment (input) / Generated Comment (result) */}
        {phase === "input" ? (
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground block">
              Existing Comment
            </label>
            <div className="rounded-lg border border-border bg-muted/50 p-3 text-sm text-muted-foreground min-h-[60px] whitespace-pre-wrap">
              {iterationComment?.trim() ? (
                <span className="text-foreground">{iterationComment}</span>
              ) : (
                <span className="italic text-xs">No existing comment — AI will generate from scratch.</span>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-bold uppercase tracking-widest text-success">
                Generated Comment
              </label>
              {isTyping && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <LucideLoader2 size={11} className="animate-spin" />
                  Writing...
                </span>
              )}
            </div>
            <Textarea
              ref={generatedTextareaRef}
              value={displayedText}
              onChange={(e) => setDisplayedText(e.target.value)}
              disabled={isTyping}
              className="resize-none text-sm overflow-hidden border-success focus-visible:ring-success/20"
            />
            {!isTyping && (
              <p className="text-xs text-muted-foreground">Edit if needed, then choose how to apply it below.</p>
            )}
          </div>
        )}

        {/* Additional Instructions */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-semibold text-foreground block">
            {phase === "result" ? "Refine further" : "What should the AI focus on?"}
          </label>
          <Textarea
            value={additionalInstructions}
            onChange={(e) => {
              setAdditionalInstructions(e.target.value.slice(0, INSTRUCTIONS_LIMIT));
              autoResizeTextarea(e.target);
            }}
            ref={(el) => el && autoResizeTextarea(el)}
            placeholder="e.g. Focus on reading progress. Keep it to 2 sentences. Mention strengths first."
            className="resize-none text-sm overflow-hidden"
            disabled={busy}
          />
          <p className={`text-xs text-right ${additionalInstructions.length > INSTRUCTIONS_LIMIT * 0.9 ? "text-destructive" : "text-muted-foreground"}`}>
            {additionalInstructions.length}/{INSTRUCTIONS_LIMIT}
          </p>
          {phase === "result" && (
            <div className="flex justify-end pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleGenerate(displayedText)}
                disabled={!canRegenerate}
              >
                {isGenerating
                  ? <LucideLoader2 size={14} className="mr-1.5 animate-spin" />
                  : <LucideSend size={14} className="mr-1.5" />
                }
                Regenerate
              </Button>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isGenerating}>
              Cancel
            </Button>
            <div className="flex flex-col gap-0.5">
              <span className={`text-[11px] font-medium ${schoolLineColor}`}>
                School plan: {schoolActive ? `${schoolRemaining} AI credits` : "N/A"}
              </span>
              <span className={`text-[11px] font-medium ${personalLineColor}`}>
                Personal: {aiCreditsRemaining != null ? `${aiCreditsRemaining} AI credits` : "—"}
              </span>
            </div>
          </div>

          {phase === "input" ? (
            <Button type="button" size="sm" onClick={() => handleGenerate()} disabled={isGenerating}>
              {isGenerating
                ? <LucideLoader2 size={14} className="mr-1.5 animate-spin" />
                : <LucideSend size={14} className="mr-1.5" />
              }
              Generate
            </Button>
          ) : (
            <div className="flex items-center gap-3">
              {currentComment?.trim() && (
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAcceptAdd}
                  disabled={busy}
                  className="bg-warning hover:bg-warning/90 text-warning-foreground border-transparent"
                >
                  Add to my existing comment
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                onClick={handleAcceptReplace}
                disabled={busy}
                className="bg-info hover:bg-info/90 text-info-foreground border-transparent"
              >
                {currentComment?.trim() ? "Replace my entire comment" : "Use this comment"}
              </Button>
            </div>
          )}
        </div>

      </div>
    </Modal>
  );
}
